import { ReactNode, createContext, useContext } from 'react'
import { z } from 'zod'
import { Form } from '$components'
import { pricingVariantSchema } from '../shared/pricing-variant-schema'
import { EditPricingContent } from './content'
import { EditPricingControls } from './controls'

export enum Input {
  Variants = 'variants',
}

const editPricingSchema = z.object({
  [Input.Variants]: z.array(pricingVariantSchema).min(1, { message: 'At least one pricing variant must be configured.' }),
})

export type EditPricingSchema = z.infer<typeof editPricingSchema>

type EditPricingFormContextType = {
  onSubmit: (values: EditPricingSchema) => void
  onCancel: () => void
  isLoading?: boolean
  currency?: string
}

const EditPricingFormContext = createContext<EditPricingFormContextType | undefined>(undefined)

type EditPricingFormProps = EditPricingFormContextType & {
  children?: ReactNode
  defaultValues: EditPricingSchema
}

export function EditPricingForm({
  onSubmit,
  onCancel,
  isLoading = false,
  currency,
  defaultValues,
  children,
}: EditPricingFormProps) {
  const context: EditPricingFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
    currency,
  }

  return (
    <EditPricingFormContext.Provider value={context}>
      <Form<EditPricingSchema, typeof editPricingSchema>
        schema={editPricingSchema}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {children}
      </Form>
    </EditPricingFormContext.Provider>
  )
}

EditPricingForm.Content = EditPricingContent
EditPricingForm.Controls = EditPricingControls

export function useEditPricingForm() {
  const context = useContext(EditPricingFormContext)

  if (context === undefined) {
    throw new Error('useEditPricingForm must be used within an EditPricingFormProvider')
  }

  return context
}
